// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useState } from 'react';
import { TextArea } from '@loa/ui-components';
import type { AnnotationBinding } from './types';

interface Props {
  bindings: AnnotationBinding[];
  onChange: (bindings: AnnotationBinding[]) => void;
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'monospace',
  fontSize: 'var(--font-size-body-03)',
};

/**
 * Raw JSON view of the placed bindings — the same array the visual editor writes.
 * Lets an admin fine-tune coordinates / field types / data sources directly, or
 * paste a whole set of bindings. Edits here reflect in the overlay above; the
 * owning service re-validates the annotation map on save. Admin-only surface.
 */
export default function NewBusinessBindingsJsonEditor({ bindings, onChange }: Props) {
  const [text, setText] = useState(() => JSON.stringify(bindings ?? [], null, 2));
  const [error, setError] = useState<string | null>(null);

  // Reflect external edits (visual placement / drag). Invalid input never reaches
  // the parent, so the prop doesn't change and the in-progress text is left alone.
  useEffect(() => {
    setText(JSON.stringify(bindings ?? [], null, 2));
  }, [bindings]);

  const handleChange = (value: string) => {
    setText(value);
    try {
      const parsed = value.trim() === '' ? [] : JSON.parse(value);
      if (!Array.isArray(parsed)) {
        setError('Bindings must be a JSON array');
        return;
      }
      setError(null);
      onChange(parsed as AnnotationBinding[]);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div data-testid="nb-bindings-json-editor">
      <TextArea
        fluid
        rows={10}
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        style={monoStyle}
        error={error ? `Invalid JSON: ${error}` : undefined}
      />
    </div>
  );
}
